import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Field } from 'formik'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { isEmpty, isNil } from 'lodash'

import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { LlmModelSelect } from '@/components/forms/formik/selects/LlmModelSelect'
import { FormField } from '@/components/forms/FormField'
import {
  getSummaryConfigForPlaybook,
  loadBitFlowVersion,
} from '@/reducers/playbooks/playbook.actions'

import { seedFieldLinks } from '../helpers/fieldLinks.helpers'
import { flowSnapshotKey } from '../helpers/unsavedChanges.helpers'
import { BranchesView } from './BranchesView'
import { PromptFieldRows } from './PromptFieldRows'
import { PromptEditorFields } from './PromptEditorFields'
import { NotetakerVersionSelector } from './NotetakerVersionSelector'

const FIELDS_VIEW = 'fields'
const BRANCHES_VIEW = 'branches'

export const RowEditorForm = ({
  setFieldValue,
  values,
  flags,
  groupByAgentVisibility,
  readOnly = false,
}) => {
  const dispatch = useDispatch()
  const {
    rtnPrompt,
    rtnBitFlow,
    rtnBitFlowVersions = [],
    rtnFieldPrompts,
    rtnModelOptions = [],
    loading,
  } = useSelector((state) => state.playbook)
  const { organizationid } = useSelector((state) => state.currentUser)
  const rtnModelOptionsLoading = useSelector(
    (state) => state.prompts?.loading?.rtnModelOptions ?? false
  )
  const [view, setView] = useState(FIELDS_VIEW)
  // Held here, not in BranchesView, so switching to the field list and back
  // keeps the branch you were reading.
  const [branchSelection, setBranchSelection] = useState({})

  // Sync the form's model from the loaded shared prompt — admins change it via
  // the AI Model selector at the bottom of this section. A prompt from before
  // models were referenced by id carries only a name, so resolve that to the id
  // the selector and every save work in; this is the one place a model name is
  // read, and nothing downstream has to know about it.
  useEffect(() => {
    // Each of these seeds the baseline alongside the value: the model and the
    // shared prompt save with the flow, so the action bar has to be able to tell
    // an edit to them from the configuration as it loaded.
    const seedModel = (modelId) => {
      setFieldValue('llm_model_id', modelId)
      setFieldValue('loaded_llm_model_id', modelId)
    }

    if (rtnPrompt?.llm_model_id) {
      seedModel(rtnPrompt.llm_model_id)
    } else if (!isEmpty(rtnPrompt?.model)) {
      const fallbackOption = rtnModelOptions.find((option) => option.name === rtnPrompt.model)
      if (fallbackOption?.llm_model_id) {
        seedModel(fallbackOption.llm_model_id)
      }
    }
  }, [rtnPrompt?.llm_model_id, rtnPrompt?.model, rtnModelOptions, setFieldValue])

  // Seed the shared prompt's editable text/token fields (org 1 only — non-admins
  // never load the real prompt record, only a synthesized user_prompt preview).
  useEffect(() => {
    if (organizationid === 1 && !isNil(rtnPrompt)) {
      const seedPromptField = (field, value) => {
        setFieldValue(field, value)
        setFieldValue(`loaded_${field}`, value)
      }

      seedPromptField('system_prompt', rtnPrompt.system_prompt ?? '')
      seedPromptField('user_prompt', rtnPrompt.user_prompt ?? '')
      seedPromptField('max_response_tokens', rtnPrompt.max_response_tokens ?? null)
    }
  }, [rtnPrompt, organizationid, setFieldValue])

  const formattedModelOptions = rtnModelOptions.map((option) => ({
    value: option.llm_model_id,
    label: option.display_name,
    description: option.description,
    deprecated: option.deprecated,
  }))

  const handleModelChange = (selectedModel, action) => {
    setFieldValue('llm_model_id', action.action === 'clear' ? null : selectedModel?.value ?? null)
  }

  // The rows and conditions exactly as the flow loaded them, kept in form state
  // rather than local state so the action bar (which owns Discard, and lives a
  // level up) reads the same baseline this editor seeded from. The key stamps
  // which flow the baseline belongs to, so a form that hasn't been re-seeded yet
  // for the flow on screen doesn't read as changed.
  const seedDiscardBaseline = (fields, links) => {
    setFieldValue('loaded_prompt_fields', fields)
    setFieldValue('loaded_field_links', links)
    setFieldValue('loaded_flow_key', flowSnapshotKey(rtnBitFlow))
  }

  useEffect(() => {
    if (!rtnBitFlow) {
      return
    }
    // A flow with no fields still has to replace whatever was on screen, or the
    // previously loaded flow's (or version's) rows linger and read as unsaved
    // changes. An editable flow gets the blank starter row back so fields can be
    // added; an older version, being read-only, gets nothing.
    if (isEmpty(rtnBitFlow.fields)) {
      const emptyRows = rtnBitFlow.is_active === false ? [] : [{ key: '', value: '' }]
      setFieldValue('prompt_fields', emptyRows)
      setFieldValue('field_links', [])
      seedDiscardBaseline(emptyRows, [])
      return
    }

    const sorted = [...rtnBitFlow.fields].sort(
      (fieldA, fieldB) => (fieldA.order_id ?? 0) - (fieldB.order_id ?? 0)
    )
    // A field "runs on its own" when no other extraction field shares its
    // prompt — count prompt usage across the flow and flag the singletons.
    // (rtnFieldPrompts, hence the model, is only loaded for org 1.)
    const extractionPromptCounts = {}
    sorted.forEach((field) => {
      if (field.field_type === 'extraction' && field.prompt_uuid) {
        extractionPromptCounts[field.prompt_uuid] =
          (extractionPromptCounts[field.prompt_uuid] ?? 0) + 1
      }
    })
    const keyVals = sorted.map((field) => {
      // Individual execution is a Balto-admin-only concept — never flag it
      // for other orgs so it can't be viewed, edited, or saved by them.
      const individualExecution =
        organizationid === 1 &&
        field.field_type === 'extraction' &&
        !!field.prompt_uuid &&
        extractionPromptCounts[field.prompt_uuid] === 1
      const ownPrompt = individualExecution ? rtnFieldPrompts?.[field.prompt_uuid] : null

      return {
        // field_name/description can come back null; coalesce so downstream
        // string ops (buildPrompt, .length, row rendering) don't crash.
        key: field.field_name ?? '',
        value: field.description ?? '',
        prompt_uuid: field.prompt_uuid,
        show_to_user: field.show_to_user,
        // Fields whose description falls back to the org default flow are
        // locked in this editor — they have to be edited on the defaults.
        inheritsDefault: field.inherited_attributes?.includes('description') === true,
        // Same for an option-backed field's taxonomy: the options belong to the
        // default and every inheriting playbook resolves against them, so this
        // editor shows them without letting them be saved. Tracked separately
        // from the description — a playbook can override one and inherit the
        // other.
        inheritsTaxonomy: field.inherited_attributes?.includes('taxonomy_uuid') === true,
        // Only carry the bit-field tool attributes when present, so rows stay
        // free of undefined keys that would trip the hasChanges deep-equal.
        ...(isNil(field.field_id) ? {} : { field_id: field.field_id }),
        ...(isNil(field.field_type) ? {} : { field_type: field.field_type }),
        ...(isNil(field.data_type) ? {} : { data_type: field.data_type }),
        ...(isNil(field.taxonomy_uuid) ? {} : { taxonomy_uuid: field.taxonomy_uuid }),
        // Per-field individual execution (org 1 only). Keep these keys off
        // non-individual rows so they don't trip the hasChanges deep-equal.
        ...(individualExecution
          ? {
              individualExecution: true,
              llm_model_id: ownPrompt?.llm_model_id ?? null,
              system_prompt: ownPrompt?.system_prompt ?? '',
              user_prompt: ownPrompt?.user_prompt ?? '',
              max_response_tokens: ownPrompt?.max_response_tokens ?? null,
            }
          : {}),
      }
    })

    setFieldValue('prompt_fields', keyVals)

    // Edges whose endpoints aren't both rows here can't be shown or edited as a
    // condition, so they're dropped rather than rendered as a chip that resolves
    // to nothing.
    const links = seedFieldLinks(rtnBitFlow.edges ?? [], keyVals)
    setFieldValue('field_links', links)
    seedDiscardBaseline(keyVals, links)
  }, [rtnBitFlow])

  const handleVersionSelect = (versionId) => {
    const selected = rtnBitFlowVersions.find((version) => version.id === versionId)
    if (selected?.is_active) {
      // Reload the live flow rather than replay its history row: the history
      // resolves each version's inherited attributes as of when it was cut,
      // while an editable config has to show — and save against — today's
      // organization defaults.
      dispatch(getSummaryConfigForPlaybook(values.selected_playbook_cid, values.selected_channel))
      return
    }
    dispatch(loadBitFlowVersion(versionId))
  }

  // The two readings of the same flow. Branches only has something to say once a
  // field gates another, so the switch appears with the first condition rather
  // than sitting there offering an empty second view — and the whole of field
  // linking is behind a flag, so with it off there is no second view at all.
  const fieldLinks = values.field_links ?? []
  const showBranches = flags?.notetakerBranchingEnabled === true && !isEmpty(fieldLinks)
  // An old version loads its own fields but the CURRENT taxonomy, so the options
  // a branch would be picked from may not be the ones that version ran with.
  // Filtering by a value that might not have existed then would be a fiction,
  // so the switch stays visible and says why it's unavailable.
  const branchesUnavailable = readOnly
  const activeView = branchesUnavailable ? FIELDS_VIEW : view

  return (
    <div className="rtn-editor__prompt user-prompt">
      <div className="user-prompt-headline">
        <div>
          <h2>Edit Prompt</h2>
          <p className="muted-text">
            Customize the sections to define key elements you want to identify during calls.
          </p>
        </div>
        <div className="flex-align-end small-gap">
          {showBranches && (
            <SegmentedControl
              small
              items={[
                {
                  accessor: FIELDS_VIEW,
                  label: 'Fields',
                  active: activeView === FIELDS_VIEW,
                  onClick: () => setView(FIELDS_VIEW),
                  dataTestId: 'editor-view-fields',
                },
                {
                  accessor: BRANCHES_VIEW,
                  label: 'Branches',
                  tooltip: branchesUnavailable
                    ? 'Only field view is available for previous versions'
                    : 'View what your note runs based on your branching field values',
                  disabled: branchesUnavailable,
                  active: activeView === BRANCHES_VIEW,
                  onClick: () => setView(BRANCHES_VIEW),
                  dataTestId: 'editor-view-branches',
                },
              ]}
            />
          )}
          <NotetakerVersionSelector
            versions={rtnBitFlowVersions}
            selectedId={rtnBitFlow?.id}
            loading={loading?.rtnBitFlowVersions}
            onSelect={handleVersionSelect}
          />
        </div>
      </div>

      {activeView === BRANCHES_VIEW && showBranches ? (
        <BranchesView
          promptFields={values.prompt_fields}
          fieldLinks={fieldLinks}
          selection={branchSelection}
          onSelectionChange={setBranchSelection}
          groupByAgentVisibility={groupByAgentVisibility}
          pendingTaxonomies={values.pending_taxonomies}
          flags={flags}
          onReorder={
            readOnly ? undefined : (nextFields) => setFieldValue('prompt_fields', nextFields)
          }
          onUpdateField={
            readOnly
              ? undefined
              : (fieldKey, nextField) =>
                  setFieldValue(
                    'prompt_fields',
                    values.prompt_fields.map((field) =>
                      field.key === fieldKey ? nextField : field
                    )
                  )
          }
        />
      ) : (
        <PromptFieldRows
          setFieldValue={setFieldValue}
          values={values}
          flags={flags}
          groupByAgentVisibility={groupByAgentVisibility}
          readOnly={readOnly}
          enableFieldTools
        />
      )}

      {organizationid === 1 && (
        <div className="rtn-prompt__additional-details rtn-overall-model">
          <FormField data-testid="ai-model-dropdown-container">
            <label>Default AI Model</label>
            <Field
              data-testid="ai-model-dropdown"
              name="llm_model_id"
              component={LlmModelSelect}
              options={formattedModelOptions}
              onChange={handleModelChange}
              loading={rtnModelOptionsLoading}
              isDisabled={readOnly}
            />
          </FormField>
          <PromptEditorFields
            testId="rtn-shared-prompt-editor"
            required={Boolean(rtnPrompt?.prompt_uuid)}
            readOnly={readOnly}
          />
        </div>
      )}
    </div>
  )
}

export default withLDConsumer()(RowEditorForm)
